import { useState, useEffect, useCallback, useRef } from 'react';
import { SSE_RECONNECT_DELAY } from '../lib/constants';
import type { BotResponse } from '../types/telegram';

export type SSEStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface UseSSEOptions {
  onMessage: (response: BotResponse) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: SSEStatus) => void;
}

interface UseSSEReturn {
  status: SSEStatus;
  connect: (url: string) => void;
  disconnect: () => void;
}

export function useSSE(options: UseSSEOptions): UseSSEReturn {
  const { onMessage, onError, onStatusChange } = options;
  const [status, setStatus] = useState<SSEStatus>('disconnected');
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const urlRef = useRef<string | null>(null);

  const updateStatus = useCallback(
    (newStatus: SSEStatus) => {
      setStatus(newStatus);
      onStatusChange?.(newStatus);
    },
    [onStatusChange]
  );

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    urlRef.current = null;
    updateStatus('disconnected');
  }, [clearReconnectTimeout, updateStatus]);

  const connect = useCallback(
    (url: string) => {
      // Clean up existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      clearReconnectTimeout();

      urlRef.current = url;
      updateStatus('connecting');

      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        updateStatus('connected');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as BotResponse;
          onMessage(data);
        } catch {
          onError?.(new Error('Failed to parse SSE message'));
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        updateStatus('error');

        // Attempt reconnection
        if (urlRef.current !== null && urlRef.current !== '') {
          reconnectTimeoutRef.current = window.setTimeout(() => {
            if (urlRef.current !== null && urlRef.current !== '') {
              connect(urlRef.current);
            }
          }, SSE_RECONNECT_DELAY);
        }
      };
    },
    [clearReconnectTimeout, onMessage, onError, updateStatus]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return { status, connect, disconnect };
}
